import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { FlaskConical, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { CopyLinkButton } from "@/components/app/CopyLinkButton";
import { api, get, errMsg } from "@/lib/api";

export function SandboxPanel({ templateId }) {
  const qc = useQueryClient();
  const [link, setLink] = useState(null);
  const [busy, setBusy] = useState(false);
  const { data } = useQuery({ queryKey: ["sandbox-runs", templateId], queryFn: () => get(`/assessment-library/${templateId}/sandbox`), enabled: !!templateId, refetchInterval: 10000 });
  if (!templateId) return <p className="text-xs text-ink-muted" data-testid="sandbox-save-first">Save a draft to get a test link.</p>;
  const make = async () => {
    setBusy(true);
    try { const r = await api.post(`/assessment-library/${templateId}/sandbox`); setLink(r.data); qc.invalidateQueries({ queryKey: ["sandbox-runs", templateId] }); }
    catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return (
    <section className="panel space-y-3 p-5" data-testid="sandbox-panel">
      <div className="flex flex-wrap items-center gap-3">
        <FlaskConical size={18} className="text-brand" aria-hidden />
        <div className="flex-1"><h3 className="text-sm font-semibold">Try it yourself</h3>
          <p className="text-xs text-ink-secondary">Opens the real candidate flow. Test results are marked as a test, never count in the tracker, reports, alerts or Fit Scores, and the link stops working after 24 hours.</p></div>
        <Button size="sm" onClick={make} disabled={busy} data-testid="sandbox-create">{busy && <Loader2 size={14} className="animate-spin" />} Send me a test link</Button>
      </div>
      {link && (
        <div className="flex flex-wrap items-center gap-2 rounded-lg bg-surface-2 px-3 py-2" data-testid="sandbox-link">
          <code className="min-w-0 flex-1 truncate text-xs" data-testid="sandbox-link-url">{link.link}</code>
          <CopyLinkButton url={link.link} label="Copy test link" size="sm" testId="sandbox-copy" />
          <a href={link.link} target="_blank" rel="noreferrer" className="text-sm text-primary underline-offset-2 hover:underline" data-testid="sandbox-open">Open</a>
        </div>
      )}
      {data?.runs?.length > 0 && (
        <ul className="space-y-1 text-xs text-ink-secondary" data-testid="sandbox-runs">
          {data.runs.map((r) => (
            <li key={r.id}>Test {new Date(r.sent_at).toLocaleString()} · {r.status === "completed" ? `completed${r.results_extra?.quiz_score != null ? ` · quiz ${r.results_extra.quiz_score}/100` : ""}${r.results?.sjt ? ` · scenarios ${r.results.sjt.score}/100` : ""}` : r.status}</li>
          ))}
        </ul>
      )}
    </section>
  );
}
